import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import UsersPage from '../shared/UsersPage';
import UserProfile from '../shared/UserProfile';
import { CustomerTypeBadge } from './CustomersList';

function CustomerDetail() {
  const { id, userId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // When the URL is /customers/:id/users/:userId, render the profile
  // page inside the customer-detail shell instead of the table.
  const viewingProfile = !!userId;

  useEffect(() => {
    axios.get(`/api/admin/customers/${id}`).then(res => setCustomer(res.data));
  }, [id]);

  const loadCustomer = () => {
    axios.get(`/api/admin/customers/${id}`).then(res => setCustomer(res.data));
  };

  if (!customer) return <div className="loading">טוען...</div>;

  const handedOff = customer.handoff_state === 'handed_off';
  const handoffDate = customer.handoff_at
    ? new Date(customer.handoff_at).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' })
    : null;

  return (
    <>
      <div className="breadcrumb">
        <Link to="/customers">לקוחות</Link>
        <span>/</span>
        <span>{customer.name}</span>
      </div>

      <div className="info-card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span>{customer.name}</span>
            <CustomerTypeBadge type={customer.type} />
            {handedOff && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 600,
                color: '#92400e', background: '#fef3c7', borderRadius: '999px',
                padding: '0.15rem 0.6rem',
              }}>נמסר ללקוח</span>
            )}
          </h3>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {!handedOff && (
              <button className="btn btn-secondary btn-sm" onClick={() => setShowHandoff(true)}>
                מסור פרויקט
              </button>
            )}
            <button
              onClick={() => setShowDelete(true)}
              style={{
                background: 'transparent',
                border: '1px solid #FCA5A5',
                color: '#DC2626',
                borderRadius: '6px',
                padding: '0.35rem 0.85rem',
                fontSize: '0.82rem',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              מחק לקוח
            </button>
          </div>
        </div>
        <div className="info-row">
          <span className="info-label">איש קשר</span>
          <span className="info-value">{customer.contact_name || '-'}</span>
        </div>
        <div className="info-row">
          <span className="info-label">טלפון</span>
          <span className="info-value">{customer.phone || '-'}</span>
        </div>
        <div className="info-row">
          <span className="info-label">מייל</span>
          <span className="info-value">{customer.email || '-'}</span>
        </div>
        <div className="info-row">
          <span className="info-label">הצטרף</span>
          <span className="info-value">{customer.created_at ? new Date(customer.created_at).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' }) : '-'}</span>
        </div>
      </div>

      {handedOff ? (
        <div className="info-card" style={{ borderInlineStart: '4px solid #f59e0b' }}>
          <h3 style={{ marginTop: 0 }}>הפרויקט נמסר ללקוח</h3>
          <p style={{ color: '#4b5563', lineHeight: 1.7 }}>
            הפרויקט הזה נמסר לניהול הלקוח{handoffDate ? ` בתאריך ${handoffDate}` : ''}.
            גישת חורי לנתוני הלקוח (אתרים, שערים, רכיבים, משתמשים והתראות) הוסרה
            אוטומטית. הלקוח מנהל את המערכת באופן עצמאי.
          </p>
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            כדי לקבל גישה חוזרת לצורך תמיכה, הלקוח צריך לפתוח חלון תמיכה זמני ממסך
            ההגדרות שלו.
          </p>
        </div>
      ) : customer.type === 'residential' ? (
        <>
          <ResidentialCustomerGates customerId={id} />
          {viewingProfile ? (
            <UserProfile
              usersBase={`/api/admin/customers/${id}/users`}
              userId={parseInt(userId, 10)}
              onBack={() => navigate(`/customers/${id}`)}
            />
          ) : (
            <UsersPage
              usersBase={`/api/admin/customers/${id}/users`}
              allowManagerCreation={true}
              onOpenProfile={(u) => navigate(`/customers/${id}/users/${u.id}`)}
            />
          )}
        </>
      ) : (
        <>
          <div className="section-header">
            <h3>אתרים</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ אתר חדש</button>
          </div>

          <div className="data-table" style={{ marginBottom: '2rem' }}>
            <table>
              <thead>
                <tr>
                  <th>שם אתר</th>
                  <th>סוג</th>
                  <th>כתובת</th>
                  <th>שערים</th>
                </tr>
              </thead>
              <tbody>
                {customer.sites.map(s => (
                  <tr key={s.id} onClick={() => navigate(`/sites/${s.id}`)}>
                    <td>{s.name}</td>
                    <td><CustomerTypeBadge type={s.type} /></td>
                    <td>{s.address || '-'}</td>
                    <td>{s.gates_count}</td>
                  </tr>
                ))}
                {customer.sites.length === 0 && (
                  <tr><td colSpan="4" style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>אין אתרים עדיין</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {viewingProfile ? (
            <UserProfile
              usersBase={`/api/admin/customers/${id}/users`}
              userId={parseInt(userId, 10)}
              onBack={() => navigate(`/customers/${id}`)}
            />
          ) : (
            <UsersPage
              usersBase={`/api/admin/customers/${id}/users`}
              allowManagerCreation={true}
              onOpenProfile={(u) => navigate(`/customers/${id}/users/${u.id}`)}
            />
          )}
        </>
      )}

      {showModal && (
        <NewSiteModal
          customerId={id}
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); loadCustomer(); }}
        />
      )}

      {showHandoff && (
        <HandoffModal
          customerId={id}
          customerName={customer.name}
          onClose={() => setShowHandoff(false)}
          onDone={() => { setShowHandoff(false); loadCustomer(); }}
        />
      )}

      {showDelete && (
        <DeleteCustomerModal
          customerId={id}
          customerName={customer.name}
          onClose={() => setShowDelete(false)}
          onDeleted={() => navigate('/customers')}
        />
      )}
    </>
  );
}

function DeleteCustomerModal({ customerId, customerName, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`/api/admin/customers/${customerId}/delete-preview`)
      .then(res => setPreview(res.data))
      .catch(() => setPreview({ sites: '?', gates: '?', users: '?', devices: '?' }));
  }, [customerId]);

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      await axios.delete(`/api/admin/customers/${customerId}`, {
        data: { confirmation: typed },
      });
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.error || 'מחיקה נכשלה');
      setBusy(false);
    }
  };

  const canSubmit = typed.trim() === customerName.trim() && !busy;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ color: '#b91c1c' }}>מחיקת לקוח</h2>
        <p style={{ color: '#4b5563', lineHeight: 1.65 }}>
          אתה עומד למחוק את <b>{customerName}</b> מהמערכת. <b>פעולה זו אינה הפיכה.</b>
        </p>
        {preview && (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: '10px',
            padding: '0.85rem 1rem',
            fontSize: '0.88rem',
            color: '#7f1d1d',
            marginBottom: '1rem',
            lineHeight: 1.65,
          }}>
            יימחקו אוטומטית:
            <ul style={{ margin: '0.5rem 0 0', paddingInlineStart: '1.25rem' }}>
              <li>{preview.sites} אתרים</li>
              <li>{preview.gates} שערים</li>
              <li>{preview.users} משתמשים (חוזרים שמות משתמש לפול)</li>
              <li>{preview.devices} רכיבי Shelly (ינותקו מהשערים אבל יישמרו ב-DB ל-audit)</li>
            </ul>
          </div>
        )}
        <div className="form-group">
          <label>
            כדי לאשר, הקלד בדיוק את שם הלקוח:{' '}
            <code style={{ background: '#f3f4f6', padding: '0.05rem 0.4rem', borderRadius: '4px' }}>{customerName}</code>
          </label>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
            placeholder={customerName}
            style={{ width: '100%' }}
          />
        </div>
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={submit}
            style={{
              background: canSubmit ? '#DC2626' : '#FCA5A5',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '0.55rem 1.1rem',
              fontFamily: 'inherit',
              fontSize: '0.92rem',
              fontWeight: 500,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {busy ? 'מוחק…' : 'מחק את הלקוח'}
          </button>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
}

function HandoffModal({ customerId, customerName, onClose, onDone }) {
  // Two-step confirmation: (1) type a reason, (2) confirm the
  // irreversible action. Submit is gated on a non-empty reason.
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await axios.post(`/api/admin/customers/${customerId}/handoff`, { reason: reason.trim() });
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה במסירת הפרויקט');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>מסירת פרויקט — {customerName}</h2>
        {error && <div className="login-error">{error}</div>}

        {step === 1 ? (
          <>
            <p style={{ color: '#4b5563', lineHeight: 1.6 }}>
              מסירת הפרויקט מעבירה את השליטה המלאה ללקוח ו<b>מסירה אוטומטית</b> את
              גישת חורי לנתוני הלקוח. ניתן יהיה לקבל גישה חוזרת רק דרך חלון תמיכה
              זמני שהלקוח יפתח.
            </p>
            <div className="form-group">
              <label>סיבת המסירה</label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={3}
                placeholder="לדוגמה: ההתקנה הושלמה, הלקוח אושר לניהול עצמאי"
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                disabled={!reason.trim()}
                onClick={() => setStep(2)}
              >המשך</button>
              <button className="btn btn-secondary" onClick={onClose}>ביטול</button>
            </div>
          </>
        ) : (
          <>
            <p style={{ color: '#b91c1c', fontWeight: 600, lineHeight: 1.6 }}>
              האם אתה בטוח? פעולה זו אינה הפיכה — גישת חורי לנתוני הלקוח תוסר מיד.
            </p>
            <div className="modal-actions">
              <button className="btn btn-primary" disabled={busy} onClick={submit}>
                {busy ? 'מוסר...' : 'כן, מסור את הפרויקט'}
              </button>
              <button className="btn btn-secondary" disabled={busy} onClick={() => setStep(1)}>חזרה</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Phase 5 — admin view for a residential customer's gates.
 *
 * Hides the industrial "sites" abstraction. A private home has 1–3
 * gates; the underlying default Site row is created server-side on
 * demand and never surfaced. Each row supports inline rename, an
 * editable street address, and a delete affordance.
 */
function ResidentialCustomerGates({ customerId }) {
  const [gates, setGates] = useState(null);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // gate.id being edited

  const reload = () => {
    axios.get(`/api/admin/customers/${customerId}/residential-gates`)
      .then(res => setGates(res.data))
      .catch(err => setError(err.response?.data?.error || 'שגיאה בטעינה'));
  };

  useEffect(reload, [customerId]);

  const onDelete = async (gateId) => {
    if (!window.confirm('למחוק את השער? פעולה זו אינה הפיכה.')) return;
    try {
      await axios.delete(`/api/admin/gates/${gateId}/residential`);
      reload();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה במחיקה');
    }
  };

  return (
    <>
      <ResidentialTechnicianAssign customerId={customerId} />

      <div className="section-header">
        <h3>שערים</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ שער חדש</button>
      </div>
      {error && <div className="login-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      <div className="data-table" style={{ marginBottom: '2rem' }}>
        <table>
          <thead>
            <tr>
              <th>שם השער</th>
              <th>דגם</th>
              <th>כתובת</th>
              <th>control</th>
              <th>lock</th>
              <th>סטטוס</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {gates && gates.map(g => (
              <ResidentialGateRow
                key={g.id}
                gate={g}
                isEditing={editing === g.id}
                onEdit={() => setEditing(g.id)}
                onCancelEdit={() => setEditing(null)}
                onSaved={() => { setEditing(null); reload(); }}
                onDelete={() => onDelete(g.id)}
              />
            ))}
            {gates && gates.length === 0 && (
              <tr><td colSpan="7" style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                עדיין אין שערים — הוסיפו את הראשון בלחיצה למעלה
              </td></tr>
            )}
            {gates === null && (
              <tr><td colSpan="7" style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>טוען…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddResidentialGateModal
          customerId={customerId}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); reload(); }}
        />
      )}
    </>
  );
}

function ResidentialGateRow({ gate, isEditing, onEdit, onCancelEdit, onSaved, onDelete }) {
  const [name, setName] = useState(gate.name);
  const [address, setAddress] = useState(gate.address || '');
  const [productId, setProductId] = useState(gate.product_id || '');
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(gate.name);
    setAddress(gate.address || '');
    setProductId(gate.product_id || '');
  }, [gate.id, gate.name, gate.address, gate.product_id]);

  useEffect(() => {
    if (isEditing) {
      axios.get('/api/admin/products').then(res => setProducts(res.data));
    }
  }, [isEditing]);

  const ready = gate.control_device_id && gate.lock_device_id;
  const save = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await axios.patch(`/api/admin/gates/${gate.id}/residential`, {
        name: name.trim(),
        address: address.trim() || null,
        product_id: productId || null,
      });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בשמירה');
    } finally {
      setBusy(false);
    }
  };

  if (isEditing) {
    return (
      <tr>
        <td>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
            style={{ width: '100%' }}
          />
        </td>
        <td>
          <select value={productId} onChange={e => setProductId(e.target.value)} style={{ width: '100%' }}>
            <option value="">ללא דגם</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}{p.model_number ? ` (${p.model_number})` : ''}
              </option>
            ))}
          </select>
        </td>
        <td colSpan="3">
          <input
            value={address}
            onChange={e => setAddress(e.target.value)}
            placeholder="לדוגמה: רחוב הראשונים 15, נתיבות"
            style={{ width: '100%' }}
          />
        </td>
        <td colSpan="2" style={{ whiteSpace: 'nowrap' }}>
          <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>
            {busy ? 'שומר…' : 'שמירה'}
          </button>
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={onCancelEdit} style={{ marginInlineStart: '0.4rem' }}>
            ביטול
          </button>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td style={{ fontWeight: 500 }}>{gate.name}</td>
      <td style={{ color: gate.product ? '#1B3A5C' : '#9ca3af' }}>
        {gate.product ? gate.product.name : '—'}
      </td>
      <td style={{ color: gate.address ? '#1B3A5C' : '#9ca3af' }}>{gate.address || '—'}</td>
      <td><DeviceMini present={!!gate.control_device_id} /></td>
      <td><DeviceMini present={!!gate.lock_device_id} /></td>
      <td>
        {ready
          ? <span style={{ color: '#15803D', fontSize: '0.78rem', fontWeight: 600 }}>מוכן</span>
          : <span style={{ color: '#92400E', fontSize: '0.78rem', fontWeight: 600 }}>ממתין להתקנה</span>}
      </td>
      <td style={{ whiteSpace: 'nowrap', textAlign: 'end' }}>
        <button className="btn btn-secondary btn-sm" onClick={onEdit}>עריכה</button>
        <button
          onClick={onDelete}
          style={{
            marginInlineStart: '0.4rem',
            background: 'transparent',
            border: '1px solid #FCA5A5',
            color: '#DC2626',
            borderRadius: '6px',
            padding: '0.3rem 0.7rem',
            fontSize: '0.82rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >מחק</button>
      </td>
    </tr>
  );
}

function ResidentialTechnicianAssign({ customerId }) {
  const [current, setCurrent] = useState(null);
  const [techs, setTechs] = useState([]);
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = () => {
    axios.get(`/api/admin/customers/${customerId}/residential-technician`)
      .then(res => setCurrent(res.data.technician));
  };

  useEffect(reload, [customerId]);

  const startEdit = () => {
    axios.get('/api/admin/technicians').then(res => setTechs(res.data));
    setPicked(current ? String(current.id) : '');
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`/api/admin/customers/${customerId}/residential-technician`, {
        technician_user_id: picked ? parseInt(picked, 10) : null,
      });
      reload();
      setEditing(false);
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בשיוך');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="info-card" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.2rem' }}>טכנאי מטפל</div>
          {editing ? (
            <select value={picked} onChange={e => setPicked(e.target.value)} style={{ width: '100%' }}>
              <option value="">ללא טכנאי</option>
              {techs.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          ) : (
            <div style={{ fontWeight: 500, color: current ? '#1B3A5C' : '#9ca3af' }}>
              {current ? current.name : 'עדיין לא הוקצה'}
            </div>
          )}
        </div>
        <div style={{ whiteSpace: 'nowrap' }}>
          {editing ? (
            <>
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>
                {busy ? 'שומר…' : 'שמירה'}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                disabled={busy}
                onClick={() => setEditing(false)}
                style={{ marginInlineStart: '0.4rem' }}
              >
                ביטול
              </button>
            </>
          ) : (
            <button className="btn btn-secondary btn-sm" onClick={startEdit}>
              {current ? 'שינוי' : 'שיוך טכנאי'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DeviceMini({ present }) {
  return (
    <span style={{
      display: 'inline-block',
      width: 10, height: 10, borderRadius: '50%',
      background: present ? '#22C55E' : '#CBD5E1',
    }} />
  );
}

function AddResidentialGateModal({ customerId, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [productId, setProductId] = useState('');
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/admin/products').then(res => setProducts(res.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await axios.post(
        `/api/admin/customers/${customerId}/residential-gates`,
        {
          name,
          address: address.trim() || null,
          product_id: productId || null,
        },
      );
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה ביצירת שער');
      setBusy(false);
    }
  };

  const pickedProduct = products.find(p => String(p.id) === String(productId));
  const requiresPair = !!pickedProduct?.requires_paired_install;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h2>שער חדש</h2>
        {error && <div className="login-error">{error}</div>}
        <div className="form-group">
          <label>שם השער</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
            required
            placeholder="לדוגמה: שער חצר / שער כניסה"
          />
        </div>
        <div className="form-group">
          <label>דגם המוצר</label>
          <select
            value={productId}
            onChange={e => setProductId(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">בחר דגם (אופציונלי)</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}{p.model_number ? ` (${p.model_number})` : ''}
                {p.requires_paired_install ? ' · זוג רכיבים' : ''}
              </option>
            ))}
          </select>
          {products.length === 0 && (
            <div className="form-hint" style={{ color: '#92400E' }}>
              אין עדיין מוצרים בקטלוג. הוסף מוצרים בעמוד "מוצרים".
            </div>
          )}
        </div>
        <div className="form-group">
          <label>כתובת (לא חובה)</label>
          <input
            value={address}
            onChange={e => setAddress(e.target.value)}
            placeholder="רחוב הראשונים 15, נתיבות"
          />
        </div>
        <div style={{
          fontSize: '0.82rem',
          color: '#64748b',
          background: '#F8FAFC',
          borderRadius: '8px',
          padding: '0.7rem 0.85rem',
          marginBottom: '1rem',
          lineHeight: 1.55,
        }}>
          {requiresPair
            ? 'בעת ההתקנה הטכנאי ישדך זוג רכיבי Shelly (2PM ל-control + i1 על RSK ל-lock). בינתיים השער יופיע ללקוח כ"ממתין להתקנה".'
            : 'הרכיב/ים יושדכו בעת ההתקנה הביתית. בינתיים השער יופיע ללקוח כ"ממתין להתקנה".'}
        </div>
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'יוצר…' : 'צור שער'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
        </div>
      </form>
    </div>
  );
}

function NewSiteModal({ customerId, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', address: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await axios.post(`/api/admin/customers/${customerId}/sites`, form);
    onCreated();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2>אתר חדש</h2>
        <div className="form-group">
          <label>שם האתר</label>
          <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
        </div>
        <div className="form-group">
          <label>כתובת</label>
          <input value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
        </div>
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary">צור אתר</button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
        </div>
      </form>
    </div>
  );
}

export default CustomerDetail;
